import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert, Box, Button, Card, Chip, CircularProgress,
    Collapse, Grid, IconButton, MenuItem, Stack,
    TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
import CommentIcon from "@mui/icons-material/Comment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EventIcon from "@mui/icons-material/Event";

import { useAuditUser } from "../hooks/useAuditUser";
import { getLog } from "../services/audit.service";

// ── Helpers ───────────────────────────────────────────────────
const fmtDt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

// ── Config por tipo de acción ─────────────────────────────────
const ACCION_CONFIG = {
    CREACION: { label: "Creación", color: "#22c55e", bg: "#f0fdf4", icon: <AddCircleOutlineIcon sx={{ fontSize: 13 }} /> },
    ASIGNACION: { label: "Asignación", color: "#3b82f6", bg: "#eff6ff", icon: <PersonIcon sx={{ fontSize: 13 }} /> },
    REASIGNACION: { label: "Reasignación", color: "#f59e0b", bg: "#fff7ed", icon: <SwapCallsIcon sx={{ fontSize: 13 }} /> },
    CAMBIO_ESTADO: { label: "Cambio de estado", color: "#8b5cf6", bg: "#f5f3ff", icon: <FiberManualRecordIcon sx={{ fontSize: 13 }} /> },
    COMENTARIO: { label: "Comentario", color: "#64748b", bg: "#f8fafc", icon: <CommentIcon sx={{ fontSize: 13 }} /> },
    ADJUNTO: { label: "Adjunto", color: "#06b6d4", bg: "#ecfeff", icon: <AttachFileIcon sx={{ fontSize: 13 }} /> },
    CAMBIO_FECHA: { label: "Cambio de fecha", color: "#f97316", bg: "#fff7ed", icon: <EventIcon sx={{ fontSize: 13 }} /> },
    EDICION: { label: "Edición", color: "#ec4899", bg: "#fdf2f8", icon: <EditIcon sx={{ fontSize: 13 }} /> },
};

const TIPOS = Object.entries(ACCION_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label }));

// ── Export CSV ────────────────────────────────────────────────
function exportCSV(rows) {
    if (!rows?.length) return;
    const headers = ["ID", "Item", "Titulo item", "Usuario", "Acción", "Detalle", "Fecha"];
    const data = rows.map(r => [
        r.id, r.item_id, r.item_titulo,
        r.usuario_nombre || r.username,
        ACCION_CONFIG[r.tipo_accion]?.label || r.tipo_accion,
        (r.comentario || "").replace(/\n/g, " | "),
        fmtDt(r.created_at),
    ].map(v => `"${v ?? ""}"`).join(","));
    const csv = [headers.join(","), ...data].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "auditoria_log.csv"; a.click();
    URL.revokeObjectURL(url);
}

const FILTROS_INIT = { tipo_accion: "", usuario_filtro: "", item_id: "", desde: "", hasta: "" };

// ══════════════════════════════════════════════════════════════
export default function AuditLog() {
    const navigate = useNavigate();
    const { username, puedeVerTodo } = useAuditUser();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filtros, setFiltros] = useState(FILTROS_INIT);
    const [showFiltros, setShowFiltros] = useState(true);
    const [pagina, setPagina] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                username, page: pagina + 1, limit: pageSize,
                ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== "")),
            };
            const data = await getLog(params);
            setRows(data.data || []);
            setTotal(data.total || 0);
        } catch {
            setError("Error al cargar el log.");
        } finally {
            setLoading(false);
        }
    }, [username, pagina, pageSize, filtros]);

    useEffect(() => { cargar(); }, [cargar]);

    if (!puedeVerTodo) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">Sin acceso al log de auditoría.</Alert>
        </Box>
    );

    const handleFiltro = (e) => {
        setFiltros(p => ({ ...p, [e.target.name]: e.target.value }));
        setPagina(0);
    };

    const limpiar = () => { setFiltros(FILTROS_INIT); setPagina(0); };

    // ── Columnas ──────────────────────────────────────────────
    const columns = [
        {
            field: "created_at",
            headerName: "Fecha y hora",
            width: 145,
            renderCell: ({ value }) => (
                <Typography variant="caption" color="text.secondary">{fmtDt(value)}</Typography>
            ),
        },
        {
            field: "usuario_nombre",
            headerName: "Usuario",
            width: 150,
            renderCell: ({ row }) => (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <PersonIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                    <Typography variant="body2" noWrap>
                        {row.usuario_nombre || row.username}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "tipo_accion",
            headerName: "Acción",
            width: 155,
            renderCell: ({ value }) => {
                const cfg = ACCION_CONFIG[value] || { label: value, color: "#64748b", bg: "#f8fafc", icon: null };
                return (
                    <Chip
                        icon={cfg.icon}
                        label={cfg.label}
                        size="small"
                        sx={{
                            backgroundColor: cfg.bg,
                            color: cfg.color,
                            fontWeight: 600,
                            fontSize: "0.70rem",
                            "& .MuiChip-icon": { color: cfg.color },
                        }}
                    />
                );
            },
        },
        {
            field: "item_titulo",
            headerName: "Ítem",
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => (
                <Stack
                    direction="row" alignItems="center" gap={0.5}
                    sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
                    onClick={() => navigate(`../${row.item_id}`)}
                >
                    <Typography variant="caption" color="text.disabled" fontWeight={700}>
                        #{row.item_id}
                    </Typography>
                    <Typography variant="body2" noWrap title={row.item_titulo}>
                        {row.item_titulo}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "comentario",
            headerName: "Detalle",
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => {
                // Armar texto descriptivo según tipo de acción
                let detalle = row.comentario || "";
                if (row.tipo_accion === "REASIGNACION")
                    detalle = `${row.controlador_anterior || "—"} → ${row.controlador_nuevo || "—"}${row.comentario ? ` · ${row.comentario}` : ""}`;
                if (row.tipo_accion === "CAMBIO_ESTADO" && row.status_nuevo_desc)
                    detalle = `${row.status_anterior_desc || "—"} → ${row.status_nuevo_desc}`;

                return (
                    <Tooltip title={detalle} arrow>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                            {detalle || "—"}
                        </Typography>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Box sx={{ backgroundColor: "background.default", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

            {/* ── Header sticky ── */}
            <Box sx={{
                position: "sticky", top: 0, zIndex: 10,
                backgroundColor: "background.default",
                px: { xs: 1.5, md: 2.5 }, pt: { xs: 1.5, md: 2 }, pb: 1,
                borderBottom: "1px solid", borderColor: "divider",
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <IconButton size="small" onClick={() => navigate(-1)}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Box>
                            <Typography variant="h5">Log de auditoría</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Historial global de acciones · {total} registro{total !== 1 ? "s" : ""}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" gap={1}>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                            onClick={() => exportCSV(rows)} disabled={!rows.length}>
                            CSV
                        </Button>
                        <Tooltip title="Actualizar">
                            <IconButton onClick={cargar} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button variant="outlined" startIcon={<FilterListIcon />}
                            onClick={() => setShowFiltros(v => !v)}
                            color={showFiltros ? "primary" : "inherit"}>
                            Filtros
                        </Button>
                    </Stack>
                </Stack>

                {/* Filtros */}
                <Collapse in={showFiltros}>
                    <Card sx={{ mb: 1 }}>
                        <Box sx={{ p: "10px 14px" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="overline">Filtros</Typography>
                                <IconButton size="small" onClick={() => setShowFiltros(false)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Grid container spacing={1.5} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField select fullWidth label="Tipo de acción" name="tipo_accion"
                                        value={filtros.tipo_accion} onChange={handleFiltro}>
                                        <MenuItem value="">Todas</MenuItem>
                                        {TIPOS.map(t => (
                                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                {/*                                 <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth label="Usuario" name="usuario_filtro"
                                        value={filtros.usuario_filtro} onChange={handleFiltro}
                                        placeholder="username..." />
                                </Grid> */}
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth label="N° de ítem" name="item_id"
                                        value={filtros.item_id} onChange={handleFiltro}
                                        type="number" inputProps={{ min: 1 }} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth type="date" label="Desde" name="desde"
                                        value={filtros.desde} onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth type="date" label="Hasta" name="hasta"
                                        value={filtros.hasta} onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button variant="text" color="inherit" onClick={limpiar} size="small">
                                        Limpiar
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Card>
                </Collapse>

                {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            </Box>

            {/* ── Tabla ── */}
            <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, flex: 1 }}>
                <Card sx={{ overflow: "hidden" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        rowCount={total}
                        loading={loading}
                        paginationMode="server"
                        paginationModel={{ page: pagina, pageSize }}
                        onPaginationModelChange={({ page, pageSize: ps }) => {
                            setPagina(page);
                            setPageSize(ps);
                        }}
                        pageSizeOptions={[20, 50, 100]}
                        disableRowSelectionOnClick
                        getRowId={r => r.id}
                        rowHeight={46}
                        hideFooterSelectedRowCount
                        sx={{ border: "none" }}
                        localeText={{
                            noRowsLabel: "No hay registros para mostrar",
                            MuiTablePagination: {
                                labelRowsPerPage: "Por página:",
                                labelDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
                            },
                        }}
                        autoHeight
                    />
                </Card>
            </Box>
        </Box>
    );
}